import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import DynamicHeadTag from "components/__new/common/DynamicHeadTag";
import { ButtonLoader, Input, SelectInput } from "components/__new/common/form";
import ComplianceHeader from "components/__new/compliance/ComplianceHeader";
import { WelcomePopup } from "components/__new/compliance/WelcomePopup";
import { toast } from "react-toastify";
import useLoginInfo from "hooks/useLoginInfo";
import validate from "utils/validate";
import PageContent from "components/__new/common/PageContent";
import Maybe from "components/__new/common/Maybe";
import useUrlQueryParams from "hooks/useUrlQueryParams";
import * as complianceActions from "store/entities/compliance/action";
import * as accountActions from "store/auth/account/action";


const BusinessDetails = () => {
    const history = useHistory();
    const dispatch = useDispatch();
    const {userInfo} = useLoginInfo();
    const {urlQueryParams} = useUrlQueryParams();
    const { employerInfoLoading, employerInfo, companyInfoUpdateLoading, companyInfoUpdate } = useSelector((s) => s.auth.account);
    const { complianceProgress, socialsLoading, socials } = useSelector((s) => s.entities.compliance);
    
    
    const [exitAfterSave, setExitAfterSave] = useState(true);
    const [isFormValidated, setIsFormValidated] = useState(false);
    const [isOpenWelcomePopup, setIsOpenWelcomePopup] = useState(false);
    const [form, setForm] = useState({
        businessName: "",
        businessDescription: "",
        businessSize: "",
        businessType: "",
        industryType: "",
        otherIndustryType: "",
        websiteOptional: "",
        facebook: "",
        twitter: "",
        otherUrl: "",
    })
    
    
    const checkFormValidation = (data = form, appendKey, appendValue) => {
        let params = {...data};
        if (appendKey){ params = {...params, [appendKey]: appendValue}; }
        if (params.industryType !== "others"){ delete params["otherIndustryType"]; }
        
        const errors = validate(params);
        if (errors){
            setIsFormValidated(false);
            // console.log("checkFormValidation", errors);
        }
        else{
            setIsFormValidated(true);
        }
    }
    
    
    const isSocialsUpdated = () => {
        let response = false;
        const socials = employerInfo?.data?.employer?.company?.socials;
        const allSocialsForm = (form.websiteOptional + form.facebook + form.twitter + form.otherUrl);
        const allSocialsDb = (socials?.website + socials?.facebook + socials?.twitter + socials?.others);
        
        if (socials){
            if (allSocialsForm !== allSocialsDb){
                response = true;
            }
        }
        else{
            if (allSocialsForm !== ""){
                response = true;
            }
        }
        
        return response;
    }
    
    
    const saveDraft = () => {
        submitForm(undefined, false);
    }
    
    
    const submitForm = async (e, exit=true) => {
        e?.preventDefault();
        setExitAfterSave(exit);
        
        const paramsCompany = {
            companyName: form.businessName,
            companyDescription: form.businessDescription,
            companySize: form.businessSize,
            companyType: form.businessType,
            industry: form.industryType,
        }
        dispatch(accountActions.updateCompanyInfo(paramsCompany));
        
        if (isSocialsUpdated() === true){
            const socials = {
                website: form.websiteOptional,
                facebook: form.facebook,
                twitter: form.twitter,
                others: form.otherUrl,
            }
            dispatch(complianceActions.addSocials(socials));
        }
    }
    
    
    const getLoadingStatus = () => {
        if (companyInfoUpdateLoading === true || socialsLoading === true){
            return true;
        }
        return false;
    }
    
    
    const getEmployerInfo = () => {
        if(!employerInfoLoading && (Object.keys(employerInfo).length === 0 || employerInfo.status !== "success")){
            dispatch(accountActions.getEmployerInfo())
        }
    }
    
    
    useEffect(() => {
        const {isCompliant, status} = userInfo.employer;
        
        if (isCompliant === true){
            history.push("/compliance/approval/success");
        }
        else if (isCompliant === false && status === "review"){
            history.push("/compliance/registered/approval");
        }
        else if (isCompliant === false && status === "rejected" && urlQueryParams?.redirect !== undefined){
            history.push("/compliance/approval/rejected");
        }
        else{
            if (complianceProgress > 0){
                dispatch(complianceActions.updateComplianceProgress(0));
            }
            else{
                setIsOpenWelcomePopup(true)
            }
            
            getEmployerInfo();
            checkFormValidation();
        }
        // eslint-disable-next-line
    }, [])
    
    
    useEffect(() => {
        if (employerInfoLoading === false && employerInfo.status === "success"){
            let data = {...form};
            const company = employerInfo?.data?.employer?.company ?? "";
            if (company !== ""){
                data = {
                    ...data,
                    businessName: company?.companyName ?? "",
                    businessDescription: company?.companyDescription ?? "",
                    businessSize: company?.companySize ?? "",
                    businessType: company?.companyType ?? "",
                    industryType: company?.industry ?? "",
                    otherIndustryType: company?.otherIndustry ?? "",
                    websiteOptional: company?.socials?.website ?? "",
                    facebook: company?.socials?.facebook ?? "",
                    twitter: company?.socials?.twitter ?? "",
                    otherUrl: company?.socials?.others ?? "",
                }
                setForm(data);
                checkFormValidation(data);
            }
        }
        // eslint-disable-next-line
    }, [employerInfoLoading, employerInfo])
    
    
    useEffect(() => {
        if (companyInfoUpdate.status === "success"){
            const socialsFilled = isSocialsUpdated();
            if ((socialsFilled === false) || (socialsFilled === true && socials?.status === "success")){
                dispatch(accountActions.resetUpdateCompanyInfo());
                dispatch(complianceActions.resetAddSocials());
                
                let data = companyInfoUpdate?.data;
                if (socialsFilled === true){
                    data = {
                        ...companyInfoUpdate?.data,
                        socials: socials?.data?.company?.socials,
                    }
                }
                dispatch(accountActions.updateEmployerInfoFE(data));
                
                if (exitAfterSave === true){
                    if (form.businessType === "registered business"){
                        history.push("/compliance/registered/verify-business");
                    }
                    else if (form.businessType === "freelance business (not registered)"){
                        history.push("/compliance/unregistered/verify-business");
                    }
                }
                else{
                    toast.success("Your data has been saved");
                }
            }
        }
        // eslint-disable-next-line
    }, [companyInfoUpdate, socials])
    
    
    return (
        <div>
            
            <DynamicHeadTag
                headerText="Earnipay Compliance"
                headerDescription="Complete each step to access all Earnipay features"
            />
            
            <WelcomePopup
                isOpen={isOpenWelcomePopup}
                setIsOpen={setIsOpenWelcomePopup}
            />
            
            <ComplianceHeader />
            
            <div className="pt-6 px-6 pb-24 sm:pt-10 sm:px-10">
                <div className="max-w-2xl mx-auto box">
                    <div className="pb-2 border-b">
                        <div className="font-bold text-xl">
                            Business Details
                        </div>
                        <div className="text-fade text-sm">
                            Fill in your company details to continue
                        </div>
                    </div>
                        
                    <PageContent
                        loading={employerInfoLoading}
                        data={employerInfo}
                        loadingText="Fetching employer information..."
                        emptyText="Employer information not found"
                        errorText="Unable to fetch employer information"
                    />

                    <Maybe condition={employerInfoLoading === false && employerInfo?.status === "success"}>
                        <form onSubmit={submitForm} className="mt-8">
                            <Input type="text" label="Business Name" placeholder="Enter Business Name" required={true} value={form.businessName} onChange={(e) => setForm({...form, businessName: e.target.value})} validationName="businessName" checkFormValidation={checkFormValidation} />
                            <Input type="text" label="Business Description" placeholder="Enter Description" required={true} value={form.businessDescription} onChange={(e) => setForm({...form, businessDescription: e.target.value})} validationName="businessDescription" checkFormValidation={checkFormValidation} />
                            
                            <SelectInput label="Business Size" required={true} value={form.businessSize} onChange={(e) => setForm({...form, businessSize: e.target.value})} validationName="businessSize" checkFormValidation={checkFormValidation}>
                                <option value="">Select number of employees</option>
                                <option value="1">1 - 10</option>
                                <option value="2">11 - 50</option>
                                <option value="3">51 - 200</option>
                                <option value="4">201 - 500</option>
                                <option value="5">500+</option>
                            </SelectInput>
                                
                            <div className="mt-4 sm:grid sm:grid-cols-2 sm:gap-x-6">
                                <SelectInput label="Business Type" required={true} value={form.businessType} onChange={(e) => setForm({...form, businessType: e.target.value})} validationName="businessType" checkFormValidation={checkFormValidation}>
                                    <option value="">Select Type</option>
                                    <option value="registered business">Registered Business</option>
                                    <option value="freelance business (not registered)">Freelance Business (Not Registered)</option>
                                </SelectInput>
                                <SelectInput label="Industry Type" required={true} value={form.industryType} onChange={(e) => setForm({...form, industryType: e.target.value})} validationName="industryType" checkFormValidation={checkFormValidation}>
                                    <option value="">Select Type</option>
                                    <option value="agriculture & forestry/wildlife">Agriculture & Forestry/Wildlife</option>
                                    <option value="business & information">Business & Information</option>
                                    <option value="construction/utilities/contracting">Construction/Utilities/Contracting</option>
                                    <option value="education">Education</option>
                                    <option value="finance & insurance">Finance & Insurance</option>
                                    <option value="food & hospitality">Food & Hospitality</option>
                                    <option value="gaming">Gaming</option>
                                    <option value="health services">Health Services</option>
                                    <option value="motor vehicle">Motor Vehicle</option>
                                    <option value="natural resources/environmental">Natural Resources/Environmental</option>
                                    <option value="personal services">Personal Services</option>
                                    <option value="real estate & housing">Real Estate & Housing</option>
                                    <option value="safety/security & legal">Safety/Security & Legal</option>
                                    <option value="transportation">Transportation</option>
                                    <option value="others">Others</option>
                                </SelectInput>
                                <Maybe condition={form.industryType === "others"}>
                                    <div className="col-span-2">
                                        <Input type="text" label="Other Industry Type" placeholder="Enter Industry Type" required={true} value={form.otherIndustryType} onChange={(e) => setForm({...form, otherIndustryType: e.target.value})} validationName="otherIndustryType" checkFormValidation={checkFormValidation} />
                                    </div>
                                </Maybe>
                            </div>
                            
                            <Input type="email" label="Business Email Address" placeholder="Enter Email Address" required={true} value={employerInfo?.data?.employer?.email} disabled={true} />
                            <Input type="tel" label="Business Phone Number" placeholder="Enter Phone Number" required={true} value={employerInfo?.data?.employer?.phoneNumber} disabled={true} />
                            
                            <div className="font-bold mb-4">
                                Social Media (Optional) 
                            </div>
                            
                            <Input type="text" label="Website" placeholder="Enter Link" value={form.websiteOptional} onChange={(e) => setForm({...form, websiteOptional: e.target.value})} validationName="websiteOptional" checkFormValidation={checkFormValidation} />
                            <Input type="text" label="Facebook" placeholder="Enter Link" value={form.facebook} onChange={(e) => setForm({...form, facebook: e.target.value})} validationName="facebook" checkFormValidation={checkFormValidation} />
                            <Input type="text" label="Twitter" placeholder="Enter Link" value={form.twitter} onChange={(e) => setForm({...form, twitter: e.target.value})} validationName="twitter" checkFormValidation={checkFormValidation} />
                            <Input type="text" label="Others" placeholder="Enter Link" value={form.otherUrl} onChange={(e) => setForm({...form, otherUrl: e.target.value})} validationName="otherUrl" checkFormValidation={checkFormValidation} />

                            <div className="mt-8 flex justify-end space-x-4">
                                <ButtonLoader
                                    type="button"
                                    onClick={saveDraft}
                                    loading={getLoadingStatus() === true && exitAfterSave === false}
                                    className="btn btn-transparent-primary"
                                    disabled={!isFormValidated}
                                >
                                    Save a Draft
                                </ButtonLoader>
                                
                                <ButtonLoader
                                    type="submit"
                                    loading={getLoadingStatus() === true && exitAfterSave === true}
                                    className="btn btn-ep-primary"
                                    disabled={!isFormValidated}
                                >
                                    Continue
                                </ButtonLoader>
                            </div>
                        </form>
                    </Maybe>
                    
                </div>
            </div>
            
        </div>
    )
}


export default BusinessDetails;